import React, { useState } from 'react';
import { CloseButton } from '../common/CloseButton';
import './ShareLinkModal.css';

/**
 * Props for ShareLinkModal component
 */
export interface ShareLinkModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** The shareable link URL */
  shareUrl: string;
  /** Whether the link is being generated */
  isLoading?: boolean;
  /** Error message if generation failed */
  error?: string;
  /** Callback when modal is closed */
  onClose: () => void;
}

/**
 * ShareLinkModal Component
 * 
 * Modal that displays a shareable link with copy-to-clipboard functionality.
 * Appears over the song settings panel on mobile.
 */
export const ShareLinkModal: React.FC<ShareLinkModalProps> = ({
  isOpen,
  shareUrl,
  isLoading = false,
  error = '',
  onClose,
}) => {
  const [copySuccess, setCopySuccess] = useState(false);

  /**
   * Handle copy to clipboard
   */
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopySuccess(true);
      
      // Reset success message after 2 seconds
      setTimeout(() => {
        setCopySuccess(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      // Fallback: select the text
      const input = document.getElementById('share-link-input') as HTMLInputElement;
      if (input) {
        input.select();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop overlay */}
      <div 
        className="share-link-modal-backdrop" 
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Modal container */}
      <div 
        className="share-link-modal" 
        role="dialog" 
        aria-label="Share link"
        aria-modal="true"
      >
        {/* Header */}
        <div className="share-link-modal-header">
          <h3 className="share-link-modal-title">
            <i className="fa-solid fa-share-nodes"></i>
            Share Your Composition
          </h3>
          <CloseButton
            onClick={onClose}
            ariaLabel="Close share link modal"
            size="medium"
            variant="default"
          />
        </div>

        {/* Content */}
        <div className="share-link-modal-content">
          {isLoading ? (
            <div className="share-link-loading">
              <div className="share-link-spinner"></div>
              <p>Generating shareable link...</p>
            </div>
          ) : error ? (
            <div className="share-link-error">
              <i className="fa-solid fa-circle-exclamation"></i>
              <p>{error}</p>
            </div>
          ) : (
            <>
              <p className="share-link-description">
                Anyone with this link can view and play your composition:
              </p>
              
              {/* Link input */}
              <div className="share-link-input-container">
                <input
                  id="share-link-input"
                  type="text"
                  className="share-link-input"
                  value={shareUrl}
                  readOnly
                  aria-label="Shareable link"
                />
              </div>

              {/* Copy button */}
              <button
                className={`share-link-copy-button ${copySuccess ? 'success' : ''}`}
                onClick={handleCopy}
                aria-label="Copy link to clipboard"
              >
                <i className={`fa-solid ${copySuccess ? 'fa-check' : 'fa-copy'}`}></i>
                <span>{copySuccess ? 'Copied!' : 'Copy to Clipboard'}</span>
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
};
